import React, { useState, useEffect } from 'react';
import { fetchCategories, createCategory, createSubcategory } from '../api';
import DarkInput from './DarkInput';
import DarkSelect from './DarkSelect';
import FormLabel from './FormLabel';
import GhostButton from './GhostButton';
import GradientButton from './GradientButton';

// Reusable category + subcategory selector with inline "add new" support.
// onChange(patch) is called with { category, subcategory } or { subcategory } on any change.
// autoSelectFirst: if true, selects the first category when none is set yet (for create forms).
export default function CategorySelect({ category, subcategory, onChange, autoSelectFirst = false }) {
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catError, setCatError]     = useState('');

  const [addingCat, setAddingCat]   = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [savingCat, setSavingCat]   = useState(false);

  const [addingSub, setAddingSub]   = useState(false);
  const [newSubName, setNewSubName] = useState('');
  const [savingSub, setSavingSub]   = useState(false);

  useEffect(() => {
    fetchCategories()
      .then(cats => {
        setCategories(cats);
        if (autoSelectFirst && !category && cats.length > 0) {
          onChange({ category: cats[0].name, subcategory: '' });
        }
      })
      .catch(err => setCatError('Could not load categories: ' + err.message))
      .finally(() => setCatLoading(false));
  }, []); // eslint-disable-line

  const selectedCat = categories.find(c => c.name === category);

  function handleCatChange(e) {
    if (e.target.value === '__add__') { setAddingCat(true); setNewCatName(''); }
    else { onChange({ category: e.target.value, subcategory: '' }); setAddingSub(false); }
  }

  async function handleAddCat() {
    if (!newCatName.trim()) return;
    setSavingCat(true); setCatError('');
    try {
      const created = await createCategory(newCatName.trim());
      setCategories(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      onChange({ category: created.name, subcategory: '' });
      setAddingCat(false);
    } catch (err) { setCatError(err.message); }
    finally { setSavingCat(false); }
  }

  function handleSubChange(e) {
    if (e.target.value === '__add__') { setAddingSub(true); setNewSubName(''); }
    else { onChange({ subcategory: e.target.value }); setAddingSub(false); }
  }

  async function handleAddSub() {
    if (!newSubName.trim() || !selectedCat) return;
    setSavingSub(true); setCatError('');
    try {
      const created = await createSubcategory(selectedCat.id, newSubName.trim());
      setCategories(prev => prev.map(c =>
        c.id === selectedCat.id
          ? { ...c, subcategories: [...c.subcategories, created].sort((a, b) => a.name.localeCompare(b.name)) }
          : c
      ));
      onChange({ subcategory: created.name });
      setAddingSub(false);
    } catch (err) { setCatError(err.message); }
    finally { setSavingSub(false); }
  }

  return (
    <>
      <div>
        <FormLabel>Category</FormLabel>
        {addingCat ? (
          <div className="flex gap-2">
            <DarkInput
              autoFocus value={newCatName}
              onChange={e => setNewCatName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); handleAddCat(); }
                if (e.key === 'Escape') setAddingCat(false);
              }}
              placeholder="New category name" style={{ flex: 1 }}
            />
            <GhostButton type="button" onClick={() => setAddingCat(false)} style={{ padding: '8px 10px' }}>✕</GhostButton>
            <GradientButton type="button" onClick={handleAddCat} disabled={savingCat} style={{ padding: '8px 14px' }}>
              {savingCat ? '…' : 'Add'}
            </GradientButton>
          </div>
        ) : (
          <DarkSelect value={category} onChange={handleCatChange} disabled={catLoading}>
            {catLoading
              ? <option value="">Loading…</option>
              : <>
                  {!category && <option value="">— Select a category —</option>}
                  {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  <option disabled>──────────</option>
                  <option value="__add__">＋ Add new category</option>
                </>
            }
          </DarkSelect>
        )}
        {catError && <p className="text-xs mt-1.5" style={{ color: '#f87171' }}>{catError}</p>}
      </div>

      <div>
        <FormLabel>Subcategory <span style={{ color: '#4b5563', fontWeight: 400 }}>(optional)</span></FormLabel>
        {!selectedCat ? (
          <p className="text-xs mt-1" style={{ color: '#374151' }}>Select a category first</p>
        ) : addingSub ? (
          <div className="flex gap-2">
            <DarkInput
              autoFocus value={newSubName}
              onChange={e => setNewSubName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); handleAddSub(); }
                if (e.key === 'Escape') setAddingSub(false);
              }}
              placeholder="New subcategory name" style={{ flex: 1 }}
            />
            <GhostButton type="button" onClick={() => setAddingSub(false)} style={{ padding: '8px 10px' }}>✕</GhostButton>
            <GradientButton type="button" onClick={handleAddSub} disabled={savingSub} style={{ padding: '8px 14px' }}>
              {savingSub ? '…' : 'Add'}
            </GradientButton>
          </div>
        ) : (
          <DarkSelect value={subcategory} onChange={handleSubChange}>
            <option value="">None</option>
            {selectedCat.subcategories.map(s => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
            <option disabled>──────────</option>
            <option value="__add__">＋ Add new subcategory</option>
          </DarkSelect>
        )}
      </div>
    </>
  );
}
